import type { ReactNode } from 'react';
import {
  FileTextIcon,
  SquareCheckBigIcon,
  LayoutPanelTopIcon,
  LibraryIcon,
  MegaphoneIcon,
  TrendingUpIcon,
} from 'lucide-react';
import { ROUTE_PATTERNS } from '@/shared/config/routes';
import type { KnownOpenFeatureFlag } from '@/shared/config/openfeature-flags';
import { PROJECT_SECTION_LABELS } from './constants';

export interface ProjectSection {
  id: string;
  label: string;
  icon: ReactNode;
  /** When true, the section is listed but cannot be navigated to. */
  disabled?: boolean;
  /**
   * Hides the entry unless this flag is on. Typed rather than a bare string so the same
   * value can be handed to `FeatureFlagGuard`, which only accepts registered keys.
   */
  flag?: KnownOpenFeatureFlag;
}

/**
 * Gates the Delivery section. Typed against the known-flag union (via `satisfies`, so the
 * literal is preserved) so it can also be handed to `FeatureFlagGuard`, which only accepts
 * registered keys — a bare string here would silently limit the gate to hiding a nav entry.
 */
export const MEDIA_DELIVERY_FLAG = 'media_delivery' as const satisfies KnownOpenFeatureFlag;

/**
 * Gates the Performance section. See `MEDIA_DELIVERY_FLAG` for why this is `satisfies`
 * rather than a `: KnownOpenFeatureFlag` annotation: the annotation would widen the literal,
 * and each consumer's per-flag lookup relies on `PROJECT_SECTIONS` keeping the exact literal
 * so an entry carrying a flag missing from that lookup fails to compile.
 */
export const MEDIA_PERFORMANCE_FLAG = 'media_performance' as const satisfies KnownOpenFeatureFlag;

// Labels stay getters rather than plain reads: this array is built at module
// evaluation, so reading the map here would freeze English at import time.
export const PROJECT_SECTIONS = [
  {
    id: ROUTE_PATTERNS.PROJECT_SECTION_OVERVIEW,
    get label() {
      return PROJECT_SECTION_LABELS[ROUTE_PATTERNS.PROJECT_SECTION_OVERVIEW];
    },
    icon: <LayoutPanelTopIcon className="size-md" />,
  },
  {
    id: ROUTE_PATTERNS.PROJECT_SECTION_BRIEFING,
    get label() {
      return PROJECT_SECTION_LABELS[ROUTE_PATTERNS.PROJECT_SECTION_BRIEFING];
    },
    icon: <FileTextIcon className="size-md" />,
  },
  {
    id: ROUTE_PATTERNS.PROJECT_SECTION_LIBRARY,
    get label() {
      return PROJECT_SECTION_LABELS[ROUTE_PATTERNS.PROJECT_SECTION_LIBRARY];
    },
    icon: <LibraryIcon className="size-md" />,
  },
  {
    id: ROUTE_PATTERNS.PROJECT_SECTION_FOLDERS,
    get label() {
      return PROJECT_SECTION_LABELS[ROUTE_PATTERNS.PROJECT_SECTION_FOLDERS];
    },
    icon: <SquareCheckBigIcon className="size-md" />,
  },
  {
    id: ROUTE_PATTERNS.PROJECT_SECTION_DELIVERY,
    get label() {
      return PROJECT_SECTION_LABELS[ROUTE_PATTERNS.PROJECT_SECTION_DELIVERY];
    },
    icon: <MegaphoneIcon className="size-md" />,
    flag: MEDIA_DELIVERY_FLAG,
  },
  {
    id: ROUTE_PATTERNS.PROJECT_SECTION_PERFORMANCE,
    get label() {
      return PROJECT_SECTION_LABELS[ROUTE_PATTERNS.PROJECT_SECTION_PERFORMANCE];
    },
    icon: <TrendingUpIcon className="size-md" />,
    flag: MEDIA_PERFORMANCE_FLAG,
  },
] satisfies ProjectSection[];
